/* ==========================================================================
   JoyCart — AJAX Cart Drawer
   --------------------------------------------------------------------------
   Public contract (shared with PDP / product cards / other modules):

     window.JoyCart.openCartDrawer()          -> void
     window.JoyCart.closeCartDrawer()         -> void
     window.JoyCart.toggleCartDrawer()        -> void
     window.JoyCart.refreshCartDrawer()       -> Promise<cart>   (re-fetches /cart.js, rebuilds UI)
     window.JoyCart.addToCart(variantId, quantity = 1, opts = {})
                                              -> Promise<lineItem|cart>
                                                 opts.open   (default true)  open drawer after add
                                                 opts.properties (object)    line-item properties
     window.JoyCart.changeLine(key, quantity) -> Promise<cart>   (set absolute qty for a line key/id)
     window.JoyCart.removeLine(key)           -> Promise<cart>   (quantity 0)
     window.JoyCart.getCart()                 -> Promise<cart>   (raw /cart.js)
     window.JoyCart.formatMoney(cents)        -> string

   The object is defined synchronously so callers loaded before this script
   (or before DOMContentLoaded) never hit `undefined`. DOM is wired up on
   DOMContentLoaded.
   ========================================================================== */
(function () {
  "use strict";

  window.JoyCart = window.JoyCart || {};
  var JoyCart = window.JoyCart;

  /* ---- internal state --------------------------------------------------- */
  var els = {};            // cached DOM nodes (filled on DOMContentLoaded)
  var booted = false;      // DOM wired?
  var lastFocused = null;  // element to restore focus to on close
  var pendingOpen = false; // openCartDrawer() called before DOM ready

  /* ---- money ------------------------------------------------------------ */
  function formatMoney(cents) {
    var amount = (cents == null ? 0 : cents);
    // Prefer Shopify's own formatter if present (respects shop money format).
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try { return window.Shopify.formatMoney(amount); } catch (e) {}
    }
    // Then the theme's global formatter (global.js).
    if (typeof window.formatMoney === "function" && window.formatMoney !== JoyCart.formatMoney) {
      try { return window.formatMoney(amount); } catch (e) {}
    }
    // Fallback: use the shop money format string if available.
    var fmt = (window.theme && window.theme.moneyFormat) || "${{amount}}";
    var value = (amount / 100).toFixed(2);
    // Add thousands separators to the integer part.
    var parts = value.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    value = parts.join(".");
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }
  JoyCart.formatMoney = formatMoney;

  /* ---- helpers ---------------------------------------------------------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function escapeHtml(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function jsonHeaders() {
    return { "Content-Type": "application/json", "Accept": "application/json" };
  }

  /* ---- header count badge (works with both class names used in theme) --- */
  function updateHeaderCount(count) {
    var badges = $all(".cart-badge, .cart-count");
    badges.forEach(function (b) { b.textContent = count; });
    if (els.drawerCount) els.drawerCount.textContent = count;
    // expose for other modules / debugging
    JoyCart.itemCount = count;
  }

  /* ---- network ---------------------------------------------------------- */
  function getCart() {
    return fetch("/cart.js", {
      method: "GET",
      headers: { "Accept": "application/json" },
      credentials: "same-origin"
    }).then(function (r) { return r.json(); });
  }
  JoyCart.getCart = getCart;

  /* ---- rendering -------------------------------------------------------- */
  function lineItemHtml(item) {
    var img = "";
    if (item.image) {
      // request a reasonably sized thumbnail
      var src = item.image;
      if (src.indexOf("?") === -1) {
        src = src.replace(/(\.[a-zA-Z]+)(\?|$)/, "_160x$1$2");
      }
      img =
        '<div class="cart-drawer-item-img">' +
        '<img src="' + escapeHtml(src) + '" alt="' + escapeHtml(item.product_title || item.title || "") + '" loading="lazy" width="72" height="72">' +
        "</div>";
    } else {
      img =
        '<div class="cart-drawer-item-img">' +
        '<span class="cart-drawer-item-img-placeholder"><span class="material-symbols-outlined" aria-hidden="true">image</span></span>' +
        "</div>";
    }

    // variant / options line
    var variantLine = "";
    if (item.product_has_only_default_variant !== true && item.variant_title) {
      variantLine = '<span class="cart-drawer-item-variant">' + escapeHtml(item.variant_title) + "</span>";
    }
    // selling plan (subscriptions)
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      variantLine += '<span class="cart-drawer-item-variant">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + "</span>";
    }

    // per-unit price (show compare-at strike when discounted)
    var unitHtml = "";
    var hasUnitDiscount = item.original_price != null && item.original_price > item.final_price;
    if (hasUnitDiscount) {
      unitHtml =
        '<span class="cart-drawer-item-unit-price">' +
        "<s>" + formatMoney(item.original_price) + "</s>" +
        '<span class="is-sale">' + formatMoney(item.final_price) + "</span>" +
        "</span>";
    } else {
      unitHtml = '<span class="cart-drawer-item-unit-price">' + formatMoney(item.final_price) + "</span>";
    }

    // line price (right column) + line-level discount labels
    var lineHasDiscount = item.original_line_price != null && item.original_line_price > item.final_line_price;
    var priceHtml = "";
    if (lineHasDiscount) {
      priceHtml =
        '<div class="cart-drawer-item-price">' +
        "<s>" + formatMoney(item.original_line_price) + "</s>" +
        formatMoney(item.final_line_price) +
        "</div>";
    } else {
      priceHtml = '<div class="cart-drawer-item-price">' + formatMoney(item.final_line_price) + "</div>";
    }

    var discountLabels = "";
    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
      item.line_level_discount_allocations.forEach(function (alloc) {
        var title = alloc.discount_application && alloc.discount_application.title ? alloc.discount_application.title : "Discount";
        discountLabels +=
          '<span class="cart-drawer-item-discount">' +
          escapeHtml(title) + " (−" + formatMoney(alloc.amount) + ")" +
          "</span>";
      });
    }

    var key = escapeHtml(item.key);

    return (
      '<li class="cart-drawer-item" data-cart-drawer-line data-line-key="' + key + '">' +
        '<a href="' + escapeHtml(item.url) + '" class="cart-drawer-item-img-link" tabindex="-1" aria-hidden="true">' + img + "</a>" +
        '<div class="cart-drawer-item-info">' +
          '<a href="' + escapeHtml(item.url) + '" class="cart-drawer-item-title">' + escapeHtml(item.product_title || item.title) + "</a>" +
          variantLine +
          unitHtml +
          '<div class="cart-drawer-qty" data-line-qty>' +
            '<button type="button" class="cart-drawer-qty-btn" data-line-decrease aria-label="Decrease quantity">' +
              '<span class="material-symbols-outlined" aria-hidden="true">remove</span>' +
            "</button>" +
            '<span class="cart-drawer-qty-value" data-line-count aria-live="polite">' + item.quantity + "</span>" +
            '<button type="button" class="cart-drawer-qty-btn" data-line-increase aria-label="Increase quantity">' +
              '<span class="material-symbols-outlined" aria-hidden="true">add</span>' +
            "</button>" +
          "</div>" +
        "</div>" +
        '<div class="cart-drawer-item-end">' +
          priceHtml +
          discountLabels +
          '<button type="button" class="cart-drawer-item-remove" data-line-remove aria-label="Remove ' + escapeHtml(item.product_title || item.title) + '">' +
            '<span class="material-symbols-outlined" aria-hidden="true">delete</span>' +
            "<span>Remove</span>" +
          "</button>" +
        "</div>" +
      "</li>"
    );
  }

  function renderShipBar(cart) {
    if (!els.shipBar) return;
    if (els.drawer && els.drawer.getAttribute("data-show-free-ship") !== "true") {
      els.shipBar.hidden = true;
      return;
    }
    var thresholdDollars = parseFloat(els.drawer ? els.drawer.getAttribute("data-free-ship-threshold") : 0) || 0;
    if (thresholdDollars <= 0) {
      els.shipBar.hidden = true;
      return;
    }
    var thresholdCents = Math.round(thresholdDollars * 100);
    var total = cart.total_price || 0;
    var remaining = thresholdCents - total;
    els.shipBar.hidden = false;

    if (remaining <= 0) {
      els.shipBar.classList.add("is-unlocked");
      if (els.shipBarMsg) els.shipBarMsg.innerHTML = "🎉 You've unlocked <strong>free shipping</strong>!";
      if (els.shipBarFill) els.shipBarFill.style.width = "100%";
      if (els.shipBarProgress) els.shipBarProgress.setAttribute("aria-valuenow", "100");
    } else {
      els.shipBar.classList.remove("is-unlocked");
      if (els.shipBarMsg) {
        els.shipBarMsg.innerHTML =
          "You're <strong>" + formatMoney(remaining) + "</strong> away from free shipping";
      }
      var pct = Math.max(0, Math.min(100, Math.round((total / thresholdCents) * 100)));
      if (els.shipBarFill) els.shipBarFill.style.width = pct + "%";
      if (els.shipBarProgress) els.shipBarProgress.setAttribute("aria-valuenow", String(pct));
    }
  }

  function renderCart(cart) {
    updateHeaderCount(cart.item_count);

    if (!booted) return; // nothing to draw yet

    var isEmpty = !cart.items || cart.items.length === 0;

    if (isEmpty) {
      if (els.itemsList) els.itemsList.innerHTML = "";
      if (els.emptyState) els.emptyState.hidden = false;
      if (els.footer) els.footer.hidden = true;
      if (els.shipBar) els.shipBar.hidden = true;
    } else {
      if (els.emptyState) els.emptyState.hidden = true;
      if (els.itemsList) {
        els.itemsList.innerHTML = cart.items.map(lineItemHtml).join("");
      }
      if (els.footer) els.footer.hidden = false;
      if (els.subtotal) els.subtotal.textContent = formatMoney(cart.total_price);
      renderShipBar(cart);
    }
  }

  function refreshCartDrawer() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    }).catch(function (err) {
      // keep UI usable; surface for debugging
      if (window.console) console.error("[JoyCart] refresh failed", err);
      throw err;
    });
  }
  JoyCart.refreshCartDrawer = refreshCartDrawer;

  /* ---- open / close ----------------------------------------------------- */
  function openCartDrawer() {
    if (!booted) { pendingOpen = true; return; }
    if (!els.drawer) return;
    lastFocused = document.activeElement;
    els.drawer.classList.add("is-open");
    els.drawer.setAttribute("aria-hidden", "false");
    if (els.overlay) {
      els.overlay.hidden = false;
      // force reflow so the transition runs
      void els.overlay.offsetWidth;
      els.overlay.classList.add("is-open");
    }
    document.body.style.overflow = "hidden";
    document.documentElement.classList.add("cart-drawer-open");
    // refresh contents whenever we open
    refreshCartDrawer();
    // focus the close button for accessibility
    window.setTimeout(function () {
      if (els.closeBtn) els.closeBtn.focus();
    }, 60);
    document.dispatchEvent(new CustomEvent("joycart:open"));
  }
  JoyCart.openCartDrawer = openCartDrawer;

  function closeCartDrawer() {
    if (!els.drawer) return;
    els.drawer.classList.remove("is-open");
    els.drawer.setAttribute("aria-hidden", "true");
    if (els.overlay) {
      els.overlay.classList.remove("is-open");
      window.setTimeout(function () {
        if (els.overlay && !els.overlay.classList.contains("is-open")) els.overlay.hidden = true;
      }, 320);
    }
    document.body.style.overflow = "";
    document.documentElement.classList.remove("cart-drawer-open");
    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus();
    }
    document.dispatchEvent(new CustomEvent("joycart:close"));
  }
  JoyCart.closeCartDrawer = closeCartDrawer;

  function toggleCartDrawer() {
    if (els.drawer && els.drawer.classList.contains("is-open")) closeCartDrawer();
    else openCartDrawer();
  }
  JoyCart.toggleCartDrawer = toggleCartDrawer;

  function showLoading(on) {
    if (els.loading) els.loading.hidden = !on;
  }

  /* ---- mutations -------------------------------------------------------- */
  function addToCart(variantId, quantity, opts) {
    quantity = quantity == null ? 1 : quantity;
    opts = opts || {};
    var open = opts.open !== false;

    var body = { id: variantId, quantity: quantity };
    if (opts.properties) body.properties = opts.properties;
    if (opts.selling_plan) body.selling_plan = opts.selling_plan;

    return fetch("/cart/add.js", {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            // Shopify returns { status, message, description } on error
            var msg = data && (data.description || data.message) ? (data.description || data.message) : "Could not add to cart";
            var error = new Error(msg);
            error.data = data;
            throw error;
          }
          return data;
        });
      })
      .then(function (lineItem) {
        return refreshCartDrawer().then(function () {
          if (open) openCartDrawer();
          document.dispatchEvent(new CustomEvent("joycart:added", { detail: { item: lineItem } }));
          return lineItem;
        });
      })
      .catch(function (err) {
        if (window.console) console.error("[JoyCart] addToCart failed", err);
        document.dispatchEvent(new CustomEvent("joycart:error", { detail: { error: err } }));
        throw err;
      });
  }
  JoyCart.addToCart = addToCart;

  // Change a line to an absolute quantity. `key` may be the line key (string)
  // or a 1-based line index (number); we pass it through as Shopify accepts both.
  function changeLine(key, quantity) {
    showLoading(false); // per-line spinner handled via row state
    return fetch("/cart/change.js", {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({ id: String(key), quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        renderCart(cart);
        document.dispatchEvent(new CustomEvent("joycart:updated", { detail: { cart: cart } }));
        return cart;
      })
      .catch(function (err) {
        if (window.console) console.error("[JoyCart] changeLine failed", err);
        // re-sync from server so the UI never drifts
        return refreshCartDrawer();
      });
  }
  JoyCart.changeLine = changeLine;

  function removeLine(key) {
    return changeLine(key, 0);
  }
  JoyCart.removeLine = removeLine;

  /* ---- DOM wiring (DOMContentLoaded) ------------------------------------ */
  function cacheEls() {
    els.drawer = $("[data-cart-drawer]");
    els.overlay = $("[data-cart-drawer-overlay]");
    els.closeBtn = $("[data-cart-drawer-close]");
    els.itemsList = $("[data-cart-drawer-items]");
    els.emptyState = $("[data-cart-drawer-empty]");
    els.loading = $("[data-cart-drawer-loading]");
    els.footer = $("[data-cart-drawer-footer]");
    els.subtotal = $("[data-cart-drawer-subtotal]");
    els.drawerCount = $("[data-cart-drawer-count]");
    els.shipBar = $("[data-cart-drawer-shipbar]");
    els.shipBarMsg = $("[data-cart-drawer-shipbar-msg]");
    els.shipBarFill = $("[data-cart-drawer-shipbar-fill]");
    els.shipBarProgress = $("[data-cart-drawer-shipbar-progress]");
  }

  function bindEvents() {
    // Close: overlay click, close button(s), and the empty-state "continue" link
    if (els.overlay) {
      els.overlay.addEventListener("click", closeCartDrawer);
    }
    // Any element flagged data-cart-drawer-close closes the drawer.
    document.addEventListener("click", function (e) {
      var closer = e.target.closest("[data-cart-drawer-close]");
      if (closer) {
        // allow the empty-state link to navigate normally
        if (closer.tagName === "A" && closer.getAttribute("href")) {
          closeCartDrawer();
          return; // do not preventDefault — let navigation happen
        }
        e.preventDefault();
        closeCartDrawer();
      }
    });

    // Esc to close
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && els.drawer && els.drawer.classList.contains("is-open")) {
        closeCartDrawer();
      }
    });

    // Simple focus trap inside the drawer when open
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Tab") return;
      if (!els.drawer || !els.drawer.classList.contains("is-open")) return;
      var focusables = $all(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        els.drawer
      ).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Delegated line-item controls (qty +/- and remove)
    if (els.itemsList) {
      els.itemsList.addEventListener("click", function (e) {
        var row = e.target.closest("[data-cart-drawer-line]");
        if (!row) return;
        var key = row.getAttribute("data-line-key");
        var countEl = row.querySelector("[data-line-count]");
        var current = countEl ? parseInt(countEl.textContent, 10) || 0 : 0;

        if (e.target.closest("[data-line-remove]")) {
          row.classList.add("is-updating");
          removeLine(key);
          return;
        }
        if (e.target.closest("[data-line-increase]")) {
          row.classList.add("is-updating");
          changeLine(key, current + 1);
          return;
        }
        if (e.target.closest("[data-line-decrease]")) {
          row.classList.add("is-updating");
          changeLine(key, Math.max(0, current - 1));
          return;
        }
      });
    }
  }

  function bindCartIcon() {
    // The header cart link(s). We support an explicit hook plus a sensible
    // fallback so the icon opens the drawer while keeping href="/cart".
    var triggers = $all("[data-cart-drawer-open], #CartDrawerToggle, .header-cart-toggle");
    if (!triggers.length) {
      // Fallback: the cart link is the <a> wrapping the .cart-badge / cart icon.
      var badge = $(".cart-badge");
      var link = badge ? badge.closest("a") : null;
      if (!link) {
        var iconA = $('a[href$="/cart"], a[href*="/cart"]');
        link = iconA;
      }
      if (link) triggers = [link];
    }
    triggers.forEach(function (t) {
      t.addEventListener("click", function (e) {
        e.preventDefault();
        openCartDrawer();
      });
    });
  }

  function boot() {
    cacheEls();
    if (!els.drawer) {
      // No drawer markup on this page; still expose the contract (no-op UI).
      booted = true;
      return;
    }
    booted = true;
    bindEvents();
    bindCartIcon();
    // initial sync so the header count is correct and the body is pre-rendered
    refreshCartDrawer();
    if (pendingOpen) {
      pendingOpen = false;
      openCartDrawer();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-bind in the Theme Editor when the header section reloads.
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector("[data-cart-drawer]")) {
      booted = false;
      boot();
    }
  });
})();
